import { useState, useEffect, useRef } from 'react'
import { Icon } from './Layout'
import { getAnnouncements } from '../services/announcements'
import { formatPostTimestamp } from '../utils/time'

const SEEN_KEY = 'chicago_last_seen_announcement'

function newestId(list) {
  return [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.id
}

export function AnnouncementsBell() {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasUnread, setHasUnread] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    getAnnouncements()
      .then(list => {
        // Render in API order (pinned first). Unread tracking uses the newest by date.
        setItems(list)
        const lastSeen = localStorage.getItem(SEEN_KEY)
        const latest = newestId(list)
        setHasUnread(!!latest && latest !== lastSeen)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('touchstart', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('touchstart', onClickOutside)
    }
  }, [])

  const toggleOpen = () => {
    setOpen(v => !v)
    const latest = newestId(items)
    if (!open && latest) {
      localStorage.setItem(SEEN_KEY, latest)
      setHasUnread(false)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        title="Announcements"
        onClick={toggleOpen}
        className="relative flex items-center justify-center w-10 h-10 border-[3px] border-on-surface bg-surface-container text-on-surface-variant hover:bg-primary-container/10 hover:text-primary-container transition-all"
        style={{ boxShadow: '2px 2px 0px 0px var(--neo-shadow-color)' }}
      >
        <Icon name="campaign" className="text-[20px]" />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-error border-2 border-background rounded-full" />
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed left-3 right-3 top-[80px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-[60vh] sm:max-h-[420px] overflow-y-auto bg-surface border-[3px] border-on-surface z-50"
            style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
          >
            <div className="px-4 py-3 border-b-[3px] border-on-surface flex items-center gap-2">
              <Icon name="campaign" className="text-[16px]" />
              <span className="font-extrabold text-[12px] uppercase tracking-widest">Announcements</span>
            </div>

            {loading ? (
              <div className="p-4 text-[12px] text-on-surface-variant font-bold uppercase tracking-wider">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-on-surface-variant text-[12px] font-bold uppercase tracking-wider">
                No announcements yet
              </div>
            ) : (
              <div className="flex flex-col divide-y-[3px] divide-on-surface">
                {items.map(item => (
                  <div key={item.id} className="px-4 py-3 flex flex-col gap-1">
                    <h4 className="font-bold text-[13px] leading-tight text-on-surface flex items-center gap-1.5">
                      {item.isPinned && <Icon name="push_pin" filled className="text-[14px] text-primary-container shrink-0" />}
                      <span className="min-w-0">{item.title}</span>
                    </h4>
                    <p className="text-[12px] text-on-surface-variant whitespace-pre-wrap">{item.body}</p>
                    <span className="text-[10px] text-on-surface-variant/70 font-mono mt-1">
                      {formatPostTimestamp(item.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}